import { Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { hasMinRole } from '@/lib/roles'
import type { GlobalRole } from '@/services/api/auth'

interface RequireRoleProps {
  /** Minimum global role required to view the wrapped route. */
  minRole: GlobalRole
  children: ReactNode
}

/**
 * Route guard requiring the authenticated user to hold at least `minRole`.
 * Non-qualifying users are redirected to their cases (replaces SuperuserRoute's
 * hard-coded is_superuser check with the role tiers).
 */
export function RequireRole({ minRole, children }: RequireRoleProps) {
  const { user, isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground text-sm font-mono tracking-wider">
            AUTHENTICATING...
          </p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (!hasMinRole(user?.role, minRole)) {
    return <Navigate to="/cases/list" replace />
  }

  return <>{children}</>
}
